import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static values = { 
    id: Number,
    spaceId: Number,
  }
  static targets = [ 
    "statusText", "processingIndicator", "phaseText", 
    "notifyArea", "notifyOptin", "notifyStatus", "downloadArea",
    "billingAlert", "billingText", "cancelArea", "osHelpArea",
    "reservedLabel", "usedCreditsArea", "usedValue",
    "systemOverrideArea", "systemOverrideText" // 🚨 NEW TARGETS
  ]

  connect() {
    this.setupNotifications()
    this.subscribeToActionCable()
    // 🚨 Send a lightweight HEAD request every 5 minutes to renew the session
    this.keepAliveInterval = setInterval(() => {
      fetch("/keepalive", { 
        method: "GET", 
        headers: { "X-Requested-With": "XMLHttpRequest" } 
      }).catch(() => console.warn("Session keep-alive ping failed."));
    }, 5 * 60 * 1000); // 1 minute
  }

  // 🚨 DRY DISCONNECT: Called automatically by Stimulus when leaving the page, 
  // OR called manually by us when the job finishes.
  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
      console.log("Pipeline terminal state reached or page left: Unsubscribed from WebSocket.")
    }

    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
    }
  }

  subscribeToActionCable() {
    const consumer = createConsumer()
    
    // 🔒 Pass BOTH IDs to form the secure multi-tenant channel
    this.channel = consumer.subscriptions.create(
      { channel: "ConvertorStatusChannel", convertor_id: this.idValue, space_id: this.spaceIdValue }, 
      {
        received: (data) => this.handleStatusUpdate(data)
      }
    )
  }

  handleStatusUpdate(data) {
    if (data.status) {
      this.statusTextTarget.innerHTML = `Status: <strong>${data.status.toUpperCase()}</strong>`
    }

    // 🚨 NEW: Catch live system override updates mid-flight
    if (data.system_override_message) {
      if (this.hasSystemOverrideAreaTarget) {
        this.systemOverrideAreaTarget.style.display = 'block';
        this.systemOverrideTextTarget.innerHTML = data.system_override_message;
      }
    }

    // 1. FILTER LOGS INTO CLEAN PHASES
    if (data.log_line) {
      const raw = data.log_line.toLowerCase();
      let friendlyPhase = null;

      if (raw.includes("extract") || raw.includes("page") || raw.includes("pdf")) {
        friendlyPhase = "Reading document contents...";
      } else if (raw.includes("ai") || raw.includes("model") || raw.includes("prompt") || raw.includes("gemini")) {
        friendlyPhase = "Analyzing data with AI...";
      } else if (raw.includes("structur") || raw.includes("reconcil") || raw.includes("table")) {
        friendlyPhase = "Structuring financial data...";
      } else if (raw.includes("excel") || raw.includes("report") || raw.includes("xlsx")) {
        friendlyPhase = "Generating final spreadsheet...";
      }

      if (friendlyPhase) {
        this.phaseTextTarget.textContent = friendlyPhase;
      }
    }

    // 🚨 TERMINAL STATE UX FIX (Done, Error, Cancelled)
    if (['done', 'error', 'cancelled'].includes(data.status)) {
      if (this.hasReservedLabelTarget) {
        this.reservedLabelTarget.textContent = "Initial Estimate:";
      }
      if (this.hasUsedCreditsAreaTarget) {
        this.usedCreditsAreaTarget.style.display = 'block';
      }
      if (this.hasUsedValueTarget && data.actual_cost !== undefined) {
        this.usedValueTarget.textContent = data.actual_cost;
      }
      if (this.keepAliveInterval) {
        clearInterval(this.keepAliveInterval);
      }
    }

    // 2. CONVERSION FINISHED SUCCESSFULLY
    if (data.status === 'done') {
      this.processingIndicatorTarget.style.display = 'none'
      this.notifyAreaTarget.style.display = 'none'
      if (this.hasCancelAreaTarget) this.cancelAreaTarget.style.display = 'none'
      
      document.querySelectorAll('.alert:not([data-convertor-status-stimulus-target="billingAlert"])').forEach(alert => alert.remove())

      this.billingAlertTarget.style.display = 'block'
      this.billingAlertTarget.className = "alert alert-success mt-3" 
      
      let receiptMsg = `<strong>Pipeline Complete!</strong> Final cost: ${data.actual_cost} credits.`
      if (data.refund_amount > 0) receiptMsg += ` (${data.refund_amount} unused credits were refunded).`
      this.billingTextTarget.innerHTML = receiptMsg
      
      // 🚨 BUILD THE VAULT UI LIVE WITH OVERRIDE INJECTION
      if (data.download_url) {
        let overrideHtml = '';
        if (data.system_override_message) {
          overrideHtml = `
            <div class="alert alert-info py-2 mb-3 shadow-none border-info-subtle">
              <i class="fas fa-info-circle me-2"></i> ${data.system_override_message}
            </div>
          `;
        }

        this.downloadAreaTarget.innerHTML = `
          <div class="card bg-light-subtle border-primary-subtle shadow-sm">
            <div class="card-header bg-transparent border-bottom-0 pb-0">
              <h3 class="card-title text-primary"><i class="fas fa-shield-alt me-2"></i>Secure File Vault</h3>
            </div>
            <div class="card-body pt-2">
              ${overrideHtml}
              <ul class="list-unstyled space-y-2 text-sm text-secondary mb-3">
                <li><strong>Original Upload(s):</strong> ${data.input_count || 1} document(s)</li>
                <li><strong>Output File:</strong> ${data.filename}</li>
                <li><strong class="text-success"><i class="fas fa-check-circle me-1"></i> Status:</strong> Auto-downloaded converted file at ${data.completed_at}</li>
              </ul>
              <div class="alert alert-warning py-2 mb-3 shadow-none border-warning-subtle">
                <i class="fas fa-clock me-2"></i> <strong>Security Notice:</strong> This file will be permanently deleted from our servers in exactly 24 hours.
              </div>
              <a href="${data.download_url}" class="btn btn-success shadow-sm">
                <i class="fas fa-download me-2"></i> Download Again
              </a>
            </div>
          </div>
        `

        // Programmatically trigger the auto-download
        const autoLink = document.createElement('a');
        autoLink.href = data.download_url;
        autoLink.setAttribute('download', data.filename);
        document.body.appendChild(autoLink);
        autoLink.click();
        autoLink.remove(); 
      }

      this.fireNotification("Conversion Complete!", "Your Excel file has automatically downloaded.")
    }

    // 3. CONVERSION FAILED
    if (data.status === 'error') {
      this.processingIndicatorTarget.style.display = 'none'
      this.notifyAreaTarget.style.display = 'none'

      // 🚨 HIDE THE CANCEL BUTTON
      if (this.hasCancelAreaTarget) this.cancelAreaTarget.style.display = 'none'
      
      // 🚨 HIDE THE DOWNLOAD/VAULT AREA (In case it previously rendered before a late error)
      if (this.hasDownloadAreaTarget) this.downloadAreaTarget.innerHTML = ''
      
      // Clear old notices and show error state
      document.querySelectorAll('.alert:not([data-convertor-status-stimulus-target="billingAlert"])').forEach(alert => alert.remove())
      this.billingAlertTarget.style.display = 'block'
      this.billingAlertTarget.className = "alert alert-danger mt-3"
      
      const errorMsg = data.message || "Conversion failed. All reserved credits have been refunded."
      this.billingTextTarget.innerHTML = `<strong>Error:</strong> ${errorMsg}`

      this.fireNotification("Conversion Failed", "There was an error processing your PDF.")
    }

    // 4. CONVERSION CANCELLED
    if (data.status === 'cancelled') {
      this.processingIndicatorTarget.style.display = 'none'
      this.notifyAreaTarget.style.display = 'none'

      // 🚨 HIDE THE CANCEL BUTTON
      if (this.hasCancelAreaTarget) this.cancelAreaTarget.style.display = 'none'
      
      // Clear old notices and show the warning state
      document.querySelectorAll('.alert:not([data-convertor-status-stimulus-target="billingAlert"])').forEach(alert => alert.remove())
      
      this.billingAlertTarget.style.display = 'block'
      this.billingAlertTarget.className = "alert alert-warning mt-3"
      
      // We display the specific message generated by the backend (refund vs deducted)
      this.billingTextTarget.innerHTML = `<strong>Cancelled:</strong> ${data.message || 'The conversion was halted.'}`

      // Hide the cancel button if it exists dynamically
      const cancelBtn = this.element.querySelector('form.button_to')
      if (cancelBtn) cancelBtn.style.display = 'none'

      this.fireNotification("Conversion Cancelled", "The PDF conversion was halted.")
    }

    // 🚨 PLUG THE RESOURCE LEAK: Terminate the connection if we hit a final state
    if (['done', 'error', 'cancelled'].includes(data.status)) {
      this.disconnect()
    }
  }

  // --- NOTIFICATION LOGIC ---

  updateOSHelpVisibility() {
    if (!this.hasOsHelpAreaTarget) return;
    
    // Only show the OS instructions if they opted in AND the browser granted permission
    if (this.notifyOptinTarget.checked && Notification.permission === "granted") {
      this.osHelpAreaTarget.style.display = 'block';
    } else {
      this.osHelpAreaTarget.style.display = 'none';
    }
  }

  setupNotifications() {
    const isOptedIn = localStorage.getItem('convertor-notify-optin') === 'true'
    this.notifyOptinTarget.checked = isOptedIn

    if (isOptedIn && Notification.permission === 'granted') {
      this.notifyStatusTarget.textContent = 'Desktop notifications enabled.'
    }

    this.updateOSHelpVisibility()
  }

  toggleNotification() {
    const isOptedIn = this.notifyOptinTarget.checked
    localStorage.setItem('convertor-notify-optin', isOptedIn)

    if (isOptedIn) {
      if (!("Notification" in window)) {
        this.notifyStatusTarget.textContent = "Your browser doesn't support notifications."
        this.notifyOptinTarget.checked = false
        this.updateOSHelpVisibility()
        return
      }

      if (Notification.permission === "granted") {
        this.notifyStatusTarget.textContent = 'Desktop notifications enabled.'
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted") {
            this.notifyStatusTarget.textContent = 'Desktop notifications enabled.'
          } else {
            this.notifyStatusTarget.textContent = 'Notifications blocked by browser.'
            this.notifyOptinTarget.checked = false
            localStorage.setItem('convertor-notify-optin', 'false')
          }
          this.updateOSHelpVisibility()
        })
      } else {
        this.notifyStatusTarget.textContent = 'Notifications blocked in browser settings.'
        this.notifyOptinTarget.checked = false
        this.updateOSHelpVisibility()
      }
    } else {
      this.notifyStatusTarget.textContent = ''
      this.updateOSHelpVisibility()
    }
  }

  fireNotification(title, body) {
    if (this.notifyOptinTarget.checked && Notification.permission === "granted") {
      console.log("we got here at least")
      // if (document.hidden) { 
        new Notification(title, { 
          body: body,
          icon: '/favicon.ico'
        })
      // }
    }
  }
}
